import React from 'react';
import { Field, useFormikContext } from 'formik';
import { Typography } from '@mui/material';
import { generateAutocompleteItems } from '../../utils/helper';
import { AutoCompleteSelectUI, DatePickerUI, FormErrorUI, FormLabelUI, InputUI } from '../Interface';
import useParam from '../../context/ParamContext';

const FormFields = ({ autocompleteOptions, viewField, autocompleteData }) => {
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const { params } = useParam();

  const showBrigadeManagement = params.TASKLIST_SHOW_BRIGADE_MANAGEMENT === 'Y';

  const hasResponsiblePerson = values.responsiblePersonId != null;
  const hasUserGroup = values.userGroupId != null;
  const hasBrigade = showBrigadeManagement && values.brigadeId != null;
  const hasWorkers = showBrigadeManagement && (values.taskListResourceOne?.length ?? 0) > 0;

  const handleAddUsers = (selectedBrigade) => {
    const filteredR1 = (autocompleteData.resourceOne ?? []).filter(
      (item) => item.statusId === selectedBrigade && item.active
    );

    const generatedDataset = generateAutocompleteItems(filteredR1, 'name', 'id');

    setFieldValue('taskListResourceOne', generatedDataset ?? []);
  };

  return (
    <div className="grid grid-cols-5 gap-x-4 gap-y-4 mx-4 mb-5">
      <div className="col-span-5">
        <Field
          name="name"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Megnevezés" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.name} />}
          InputLabelProps={{ shrink: values.name && values.name !== '' }}
          disabled={viewField}
        />
      </div>

      <div className="col-span-5 md:col-span-2">
        <Field
          type="autocomplete"
          name="typeId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Típus" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.typeId} />}
          onChange={(_e, newVal) => {
            setFieldValue('typeId', newVal?.value ?? null).then(() => {
              validateField('typeId');
            });
          }}
          InputLabelProps={{ shrink: values.typeId !== '' }}
          selectedValue={values.typeId}
          dataset={autocompleteOptions.type}
          disabled={viewField}
        />
      </div>

      <div className="col-span-5 md:col-span-3">
        <Field
          type="autocomplete"
          name="lineId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Projekt" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.lineId} />}
          onChange={(_e, newVal) => {
            setFieldValue('lineId', newVal?.value ?? null).then(() => {
              validateField('lineId');
            });
          }}
          InputLabelProps={{ shrink: values.lineId !== '' }}
          selectedValue={values.lineId}
          dataset={autocompleteOptions.project}
          disabled={viewField}
        />
      </div>

      <div className="col-span-5 md:col-span-2">
        <Field
          type="autocomplete"
          name="statusId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Státusz" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.statusId} />}
          onChange={(_e, newVal) => {
            setFieldValue('statusId', newVal?.value ?? null).then(() => {
              validateField('statusId');
            });
          }}
          InputLabelProps={{ shrink: values.statusId !== '' }}
          selectedValue={values.statusId}
          dataset={autocompleteOptions.status}
          disabled={viewField}
        />
      </div>

      <div className="col-span-5 md:col-span-2">
        <Field
          type="date"
          name="startDate"
          component={DatePickerUI}
          value={values.startDate}
          fullWidth
          label={<FormLabelUI text="Kezdő dátum" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.startDate} />}
          onChange={(date) => {
            setFieldValue('startDate', date).then(() => {
              validateField('startDate');
            });
          }}
          InputLabelProps={{ shrink: values.startDate !== '' }}
          disabled={viewField}
        />
      </div>

      <div className="md:col-span-1 col-span-5">
        <Field
          type="number"
          name="duration"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Időintervallum (nap)" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.duration} />}
          InputLabelProps={{ shrink: values.duration !== '' }}
          disabled={viewField}
        />
      </div>

      <div className="col-span-5 md:col-span-2">
        <Field
          type="autocomplete"
          name="responsiblePersonId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Felelős" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.responsiblePersonId} />}
          onChange={(_e, newVal) => {
            setFieldValue('responsiblePersonId', newVal?.value ?? null).then(() => {
              validateField('responsiblePersonId');
            });
          }}
          InputLabelProps={{ shrink: values.responsiblePersonId !== '' }}
          selectedValue={values.responsiblePersonId}
          dataset={autocompleteOptions.user}
          disabled={viewField || hasUserGroup || hasBrigade || hasWorkers}
        />
      </div>

      <div className="col-span-5 md:col-span-2">
        <Field
          type="autocomplete"
          name="userGroupId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Felelősök (csoport)" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.userGroupId} />}
          onChange={(_e, newVal) => {
            setFieldValue('userGroupId', newVal?.value ?? null).then(() => {
              validateField('userGroupId');
            });
          }}
          InputLabelProps={{ shrink: values.userGroupId !== '' }}
          selectedValue={values.userGroupId}
          dataset={autocompleteOptions.userGroup}
          disabled={viewField || hasResponsiblePerson || hasBrigade || hasWorkers}
        />
      </div>
      {showBrigadeManagement && (
        <>
          <div className="col-span-5 md:col-span-2">
            <Field
              type="autocomplete"
              name="brigadeId"
              component={AutoCompleteSelectUI}
              fullWidth
              label={<FormLabelUI text="Brigád" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.brigadeId} />}
              onChange={(_e, newVal) => {
                handleAddUsers(newVal?.value);
                setFieldValue('brigadeId', newVal?.value ?? null);
              }}
              InputLabelProps={{ shrink: values.brigadeId != null }}
              selectedValue={values.brigadeId}
              dataset={autocompleteOptions.brigade}
              disabled={viewField || hasResponsiblePerson || hasUserGroup}
            />
          </div>
          <div className="col-span-5 md:col-span-3 flex items-center gap-2">
            <Field
              type="autocomplete"
              name="taskListResourceOne"
              component={AutoCompleteSelectUI}
              fullWidth
              label={<FormLabelUI text="Dolgozók" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.taskListResourceOne} />}
              onChange={(_e, newVal) => {
                setFieldValue('taskListResourceOne', newVal ?? []).then(() => {
                  validateField('taskListResourceOne');
                });
              }}
              InputLabelProps={{
                shrink: Boolean(values.taskListResourceOne?.length),
              }}
              selectedValue={values.taskListResourceOne ?? []}
              multiple
              freeSolo
              getOptionLabel={(option) => option.label}
              dataset={autocompleteOptions.resourceOne}
              disabled={viewField || hasResponsiblePerson || hasUserGroup}
            />

            <Typography mt="1rem">{values.taskListResourceOne?.length ?? 0}</Typography>
          </div>
        </>
      )}
    </div>
  );
};

export default FormFields;
